import Box from "@mui/joy/Box";
import { useParams } from "react-router-dom";
import { useTeam } from "../TeamLayout";
import {
  UpsertSupplierContactInput,
  useContactByUniqueIdQuery,
  useContactUpdateMutation,
} from "../../../__generated__/types-and-hooks";
import {
  Breadcrumbs,
  Card,
  CardContent,
  IconButton,
  Link,
  Sheet,
  Table,
  Typography,
} from "@mui/joy";
import { useTranslation } from "../../../i18n";
import { useCallback, useState } from "react";
import HomeIcon from "@mui/icons-material/Home";
import { Link as RouterLink } from "react-router-dom";
import QTextInput from "../../../x-components/QTextInput";
import ContactMethodsModal from "./EditContactMethodsModal";
import ModeEditIcon from "@mui/icons-material/ModeEdit";

export default function Contact() {
  const t = useTranslation();
  const teamCtx = useTeam();
  const { uniqueId } = useParams();
  const contactQuery = useContactByUniqueIdQuery({
    variables: { uniqueId: uniqueId! },
  });
  const contact = contactQuery.data && contactQuery.data.contact_by_id;
  const [contactUpdateMutation] = useContactUpdateMutation();
  const updateFn = async (
    updates: Omit<UpsertSupplierContactInput, "uniqueId" | "teamId">
  ) => {
    const res = await contactUpdateMutation({
      variables: {
        contact: {
          teamId: teamCtx.team.id,
          uniqueId: contact?.uniqueId!,
          ...updates,
        },
      },
      onCompleted(data) {
        contactQuery.refetch();
      },
    });
    return res.errors?.map((err) => err.message).join("; ");
  };

  const [modal, setModal] = useState<"edit_contact_methds" | null>(null);

  const onEditContactMethodsClick = useCallback(() => {
    setModal("edit_contact_methds");
  }, []);

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumbs"
        sx={{
          "--Breadcrumbs-gap": "4px",
          m: 1,
        }}
        size="sm"
      >
        <Link component={RouterLink} color="neutral" to="/">
          <HomeIcon />
        </Link>
        <Link
          component={RouterLink}
          color="neutral"
          to={`/${teamCtx.team.slug}`}
        >
          {teamCtx.team.name}
        </Link>
        <Link
          component={RouterLink}
          color="neutral"
          to={`/${teamCtx.team.slug}/contacts`}
        >
          {t("Contacts")}
        </Link>
        <Typography level="title-sm">#{uniqueId?.toUpperCase()}</Typography>
      </Breadcrumbs>
      <Box
        sx={{
          p: 2,
          columns: "400px",
        }}
      >
        <Card
          sx={{
            backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
            padding: 0,
            mb: 2,
            breakInside: "avoid",
          }}
          variant="outlined"
        >
          <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
            {t("Contact")}
          </Typography>
          <CardContent
            sx={{
              m: 0,
              margin: "2px",
              borderRadius: 5,
              border: "1px solid black",
              bgcolor: "background.surface",
              gap: 0,
              p: 1,
              pl: 2,
            }}
          >
            {contact && (
              <Sheet sx={{ overflow: "auto" }}>
                <Table
                  borderAxis="xBetween"
                  sx={{
                    p: 0,
                    m: 0,
                    "& tbody td:nth-child(1)": { width: 180 },
                    "& tbody td": { pl: 0 },
                  }}
                >
                  <tbody>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Name")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={contact.name}
                          update={(name) => updateFn({ name: name })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Country")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={contact.countryCode ?? ""}
                          update={(countryCode) =>
                            updateFn({ countryCode: countryCode })
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">
                          {t("Address Line 1")}
                        </Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={contact.addressLine1 ?? ""}
                          update={(addressLine1) => updateFn({ addressLine1 })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">
                          {t("Address Line 2")}
                        </Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={contact.addressLine2 ?? ""}
                          update={(addressLine2) => updateFn({ addressLine2 })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("City")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={contact.city ?? ""}
                          update={(city) => updateFn({ city })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Zip Code")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={contact.postalCode ?? ""}
                          update={(postalCode) => updateFn({ postalCode })}
                        />
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Sheet>
            )}
          </CardContent>
        </Card>
        <Card
          sx={{
            backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
            padding: 0,
            mb: 2,
            breakInside: "avoid",
          }}
          variant="outlined"
        >
          <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
            {t("Contact Methods")}
            <IconButton
              sx={{ fontSize: 12 }}
              size="sm"
              color="primary"
              variant="plain"
              onClick={onEditContactMethodsClick}
            >
              <ModeEditIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Typography>
          <CardContent
            sx={{
              m: 0,
              margin: "2px",
              borderRadius: 5,
              border: "1px solid black",
              bgcolor: "background.surface",
              gap: 0,
              p: 1,
              pl: 2,
            }}
          >
            {contact && (
              <Sheet sx={{ overflow: "auto" }}>
                {contact.contactMethods.map((cm) => (
                  <Box key={cm.key}>
                    {cm.key} - {cm.value}
                  </Box>
                ))}
              </Sheet>
            )}
          </CardContent>
        </Card>
      </Box>
      {contact && modal && (
        <ContactMethodsModal
          contact={contact}
          onClose={() => {
            setModal(null);
          }}
        />
      )}
    </>
  );
}
